import {Option} from 'chakra-multiselect';
import {type Filters} from 'pages/facilities/driver-feedback/verbatim-reviews/VerbatimReviews.types';
import dayjs from 'utils/dayjs';
import omit from 'lodash/omit';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';

const startDate = dayjs().subtract(2, 'year').format('MM/DD/YYYY');
const endDate = dayjs().format('MM/DD/YYYY');

/**
 * Constant object defining available 'sort by' options and their labels.
 *
 * @property {string} TRANSIENT - Represents a transient reservation type.
 * @property {string} MONTHLY - Represents a monthly reservation type.
 * @property {string} EVENT - Represents an event reservation type.
 *
 * @example
 * const sort = SORT_BY.SUBMITTED;
 * console.log(sort.label); // Outputs 'Submitted date'
 */
export const SORT_BY = {
    SUBMITTED: {
        label: 'Sort By Submitted Date',
        value: 'submitted',
    },
    RENTAL_ENDS: {
        label: 'Sort By Reservation End Date',
        value: 'rental_ends',
    },
    RATING_HIGH_TO_LOW: {
        label: 'Sort By Rating High To Low',
        value: 'rating_high_to_low',
    },
    RATING_LOW_TO_HIGH: {
        label: 'Sort By Rating Low To High',
        value: 'rating_low_to_high',
    },
} as const;

/**
 * Constant object defining available reservation types and their labels.
 *
 * @property {string} TRANSIENT - Represents a transient reservation type.
 * @property {string} MONTHLY - Represents a monthly reservation type.
 * @property {string} EVENT - Represents an event reservation type.
 *
 * @example
 * const reservation = RESERVATION_TYPES.TRANSIENT;
 * console.log(reservation.label); // Outputs 'Transient'
 */
export const RESERVATION_TYPES = {
    TRANSIENT: {
        value: 'transient',
        label: 'Transient',
    },
    MONTHLY: {
        value: 'monthly',
        label: 'Monthly',
    },
    EVENT: {
        value: 'event',
        label: 'Event',
    },
} as const;

export const reservationTypeSelections = Object.values(RESERVATION_TYPES);

/**
 * Constant object defining available ratings and their labels.
 *
 * @property {string} ONE - Represents a 1 rating.
 * @property {string} TWO - Represents a 2 rating.
 * @property {string} THREE - Represents a 3 rating.
 * @property {string} FOUR - Represents a 4 rating.
 * @property {string} FIVE - Represents a 5 rating.
 *
 * @example
 * const one = RATING_SELECTIONS.ONE;
 * console.log(one.label); // Outputs '1'
 */
export const RATING_SELECTIONS = {
    ONE: {value: '1', label: '1'},
    TWO: {value: '2', label: '2'},
    THREE: {value: '3', label: '3'},
    FOUR: {value: '4', label: '4'},
    FIVE: {value: '5', label: '5'},
} as const;

export const ratingSelections = Object.values(RATING_SELECTIONS);

/**
 * Constant object defining available comment options and their labels.
 *
 * @property {string} HAS_COMMENT - Represents 'has comment' option.
 * @property {string} NO_COMMENT - Represents 'no comment' option.
 *
 * @example
 * const hasComment = COMMENT_OPTIONS.HAS_COMMENT;
 * console.log(hasComment.label); // Outputs 'Has comment'
 */
export const COMMENT_OPTIONS = {
    HAS_COMMENT: {value: 'has_comment', label: 'Has comment'},
    NO_COMMENT: {value: 'no_comment', label: 'No comment'},
} as const;

export const commentSelections = Object.values(COMMENT_OPTIONS);

/**
 * Constant object defining available sentiment options and their labels.
 *
 * @property {string} POSITIVE - Represents 'positive' option.
 * @property {string} NEGATIVE - Represents 'negative' option.
 * @property {string} HIGH_IMPACT - Represents 'high impact' option.
 *
 * @example
 * const sentiment = SENTIMENT_OPTIONS.POSITIVE;
 * console.log(sentiment.label); // Outputs 'Positive'
 */
export const SENTIMENT_OPTIONS = {
    POSITIVE: {value: 'positive', label: 'Positive'},
    NEGATIVE: {value: 'negative', label: 'Negative'},
    HIGH_IMPACT: {value: 'high_impact', label: 'High impact'},
} as const;

export const sentimentSelections = Object.values(SENTIMENT_OPTIONS);

/**
 * Constant object defining available driver tag options and their labels.
 *
 * @property {string} SMOOTH_EXPERIENCE - Represents 'Smooth experience' option.
 * @property {string} GREAT_VALUE - Represents 'Great value' option.
 * @property {string} FRIENDLY_STAFF - Represents 'Friendly staff' option.
 * @property {string} CLEAR_INSTRUCTIONS - Represents 'Clear instructions' option.
 * @property {string} SAFE_SECURE - Represents 'Safe & secure' option.
 * @property {string} CLEAN_FACILITY - Represents 'Clean facility' option.
 * @property {string} COULD_NOT_PARK_HERE - Represents 'Couldn't park here' option.
 * @property {string} ISSUE_REDEEMING - Represents 'Issue redeeming' option.
 * @property {string} EXTRA_CHARGE_OR_FEE - Represents 'Extra charge or fee' option.
 * @property {string} POOR_SERVICE - Represents 'Poor service' option.
 * @property {string} HARD_TO_FIND - Represents 'Hard to find' option.
 * @property {string} SECURITY_CONCERN - Represents 'Security concern' option.
 * @property {string} POORLY_MAINTAINED - Represents 'Poorly maintained' option.
 *
 * @example
 * const tag = DRIVER_TAG_OPTIONS.SMOOTH_EXPERIENCE;
 * console.log(tag.label); // Outputs 'Smooth experience'
 */
export const DRIVER_TAG_OPTIONS = {
    SMOOTH_EXPERIENCE: {value: 'Smooth experience', label: 'Smooth experience'},
    GREAT_VALUE: {value: 'Great value', label: 'Great value'},
    FRIENDLY_STAFF: {value: 'Friendly staff', label: 'Friendly staff'},
    CLEAR_INSTRUCTIONS: {
        value: 'Clear instructions',
        label: 'Clear instructions',
    },
    SAFE_SECURE: {value: 'Safe & secure', label: 'Safe & secure'},
    CLEAN_FACILITY: {value: 'Clean facility', label: 'Clean facility'},
    COULD_NOT_PARK_HERE: {
        value: "Couldn't park here",
        label: "Couldn't park here",
    },
    ISSUE_REDEEMING: {value: 'Issue redeeming', label: 'Issue redeeming'},
    EXTRA_CHARGE_OR_FEE: {
        value: 'Extra charge or fee',
        label: 'Extra charge or fee',
    },
    POOR_SERVICE: {value: 'Poor service', label: 'Poor service'},
    HARD_TO_FIND: {value: 'Hard to find', label: 'Hard to find'},
    SECURITY_CONCERN: {value: 'Security concern', label: 'Security concern'},
    POORLY_MAINTAINED: {value: 'Poorly maintained', label: 'Poorly maintained'},
} as const;

export const driverTagSelections = Object.values(DRIVER_TAG_OPTIONS);

/**
 * Constant object defining available system tag options and their corresponding values.
 *
 /**
 * @property {string} POSITIVE_EXPERIENCE - Represents 'Positive Experience' option.
 * @property {string} SCANNER_ERROR - Represents 'Scanner Error' option.
 * @property {string} NAVIGATION_ISSUE - Represents 'Navigation Issue' option.
 * @property {string} LOT_FULL - Represents 'Lot Full' option.
 * @property {string} INFRASTRUCTURE_LIMITATION - Represents 'Infrastructure Limitation' option.
 * @property {string} ACCESS_ISSUE_GENERAL - Represents 'Access Issue' option.
 * @property {string} ACCESS_ISSUE_ELEVATOR - Represents 'Elevator Access Issue' option.
 * @property {string} ACCESS_ISSUE_GATE - Represents 'Door/Gate Access Issue' option.
 * @property {string} ACCESS_ISSUE_PEDESTRIAN - Represents 'Pedestrian Access Issue' option.
 * @property {string} LONG_WAIT - Represents 'Long Wait' option.
 * @property {string} UNEXPECTED_FEE - Represents 'Unexpected Fee' option.
 * @property {string} POOR_FACILITY_CONDITION - Represents 'Poor Facility Condition' option.
 * @property {string} POOR_PARKING_INSTRUCTIONS - Represents 'Poor Parking Instructions' option.
 * @property {string} MISSING_ATTENDANT - Represents 'Missing Attendant' option.
 * @property {string} UNFRIENDLY_STAFF - Represents 'Unfriendly Staff' option.
 * @property {string} SPOTHERO_NOT_ACCEPTED - Represents 'SpotHero Not Accepted' option.
 * @property {string} POOR_SIGNAGE - Represents 'Poor Signage' option.
 * @property {string} PAID_ABOVE_DRIVE_UP - Represents 'Paid Above Drive-Up' option.
 * @property {string} SAFETY_ISSUE - Represents 'Safety Issue' option.
 * @property {string} AIRPORT_SHUTTLE_ISSUE - Represents 'Airport Shuttle Issue' option.
 * @property {string} LOCATION_INACCESSIBLE - Represents 'Location Inaccessible' option.
 * @property {string} INCORRECT_OPERATING_HOURS - Represents 'Incorrect Operating Hours' option.
 * @property {string} RECEIVED_TICKET - Represents 'Received Ticket' option.
 * @property {string} INACCURATE_AMENITIES - Represents 'Inaccurate Amenities' option.
 * @property {string} OVERSIZE_FEE - Represents 'Oversize Fee' option.
 * @property {string} VEHICLE_SIZE_RESTRICTED - Represents 'Vehicle Size Restricted' option.
 * @property {string} POOR_LOCATION_PHOTOS - Represents 'Poor Location Photos' option.
 * @property {string} VEHICLE_TOWED - Represents 'Vehicle Towed' option.
 *
 * @example
 * const tag = SYSTEM_TAG_OPTIONS.SCANNER_ERROR;
 * console.log(tag.value); // Outputs: 'Scanner Error'
 */
export const SYSTEM_TAG_OPTIONS = {
    POSITIVE_EXPERIENCE: {
        value: 'Positive Experience',
        label: 'Positive Experience',
    },
    SCANNER_ERROR: {
        value: 'Scanner Error',
        label: 'Scanner Error',
    },
    NAVIGATION_ISSUE: {
        value: 'Navigation Issue',
        label: 'Navigation Issue',
    },
    LOT_FULL: {
        value: 'Lot Full',
        label: 'Lot Full',
    },
    INFRASTRUCTURE_LIMITATION: {
        value: 'Infrastructure Limitation',
        label: 'Infrastructure Limitation',
    },
    ACCESS_ISSUE_GENERAL: {
        value: 'Access Issue',
        label: 'Access Issue',
    },
    ACCESS_ISSUE_ELEVATOR: {
        value: 'Elevator Access Issue',
        label: 'Elevator Access Issue',
    },
    ACCESS_ISSUE_GATE: {
        value: 'Door/Gate Access Issue',
        label: 'Door/Gate Access Issue',
    },
    ACCESS_ISSUE_PEDESTRIAN: {
        value: 'Pedestrian Access Issue',
        label: 'Pedestrian Access Issue',
    },
    LONG_WAIT: {
        value: 'Long Wait',
        label: 'Long Wait',
    },
    UNEXPECTED_FEE: {
        value: 'Unexpected Fee',
        label: 'Unexpected Fee',
    },
    POOR_FACILITY_CONDITION: {
        value: 'Poor Facility Condition',
        label: 'Poor Facility Condition',
    },
    POOR_PARKING_INSTRUCTIONS: {
        value: 'Poor Parking Instructions',
        label: 'Poor Parking Instructions',
    },
    MISSING_ATTENDANT: {
        value: 'Missing Attendant',
        label: 'Missing Attendant',
    },
    UNFRIENDLY_STAFF: {
        value: 'Unfriendly Staff',
        label: 'Unfriendly Staff',
    },
    SPOTHERO_NOT_ACCEPTED: {
        value: 'SpotHero Not Accepted',
        label: 'SpotHero Not Accepted',
    },
    POOR_SIGNAGE: {
        value: 'Poor Signage',
        label: 'Poor Signage',
    },
    PAID_ABOVE_DRIVE_UP: {
        value: 'Paid Above Drive-Up',
        label: 'Paid Above Drive-Up',
    },
    SAFETY_ISSUE: {
        value: 'Safety Issue',
        label: 'Safety Issue',
    },
    AIRPORT_SHUTTLE_ISSUE: {
        value: 'Airport Shuttle Issue',
        label: 'Airport Shuttle Issue',
    },
    LOCATION_INACCESSIBLE: {
        value: 'Location Inaccessible',
        label: 'Location Inaccessible',
    },
    INCORRECT_OPERATING_HOURS: {
        value: 'Incorrect Operating Hours',
        label: 'Incorrect Operating Hours',
    },
    RECEIVED_TICKET: {
        value: 'Received Ticket',
        label: 'Received Ticket',
    },
    INACCURATE_AMENITIES: {
        value: 'Inaccurate Amenities',
        label: 'Inaccurate Amenities',
    },
    OVERSIZE_FEE: {
        value: 'Oversize Fee',
        label: 'Oversize Fee',
    },
    VEHICLE_SIZE_RESTRICTED: {
        value: 'Vehicle Size Restricted',
        label: 'Vehicle Size Restricted',
    },
    POOR_LOCATION_PHOTOS: {
        value: 'Poor Location Photos',
        label: 'Poor Location Photos',
    },
    VEHICLE_TOWED: {
        value: 'Vehicle Towed',
        label: 'Vehicle Towed',
    },
} as const;

export const systemTagSelections = Object.values(SYSTEM_TAG_OPTIONS);

/**
 * Filters a list of options based on an array of values.
 *
 * This utility function takes an array of string values and an array of Option objects,
 * then returns only the Option objects whose value property (converted to string)
 * matches any of the provided values.
 *
 * @param {string[]} values - Array of string values to match against option values
 * @param {Option[]} allOptions - Array of Option objects to filter
 * @returns {Option[]} Filtered array of Option objects that match the provided values
 *
 * @example
 * // Filter rating options to only include ratings 1 and 3
 * const selectedRatings = ['1', '3'];
 * const filteredOptions = mapValuesToOptions(selectedRatings, ratingSelections);
 * // Returns [{value: '1', label: '1'}, {value: '3', label: '3'}]
 *
 */
export const mapValuesToOptions = (
    values: string[],
    allOptions: Option[]
): Option[] => allOptions.filter((opt) => values.includes(String(opt.value)));

/**
 * Represents the full unapplied filter state, where each filter key maps to
 * an array of selected options (typically from a multiselect dropdown).
 *
 * This type is used to manage all pending filter selections that have not yet
 * been submitted/applied by the user.
 *
 * @example
 * const unappliedFilters: UnappliedFilters = {
 *   rating: [{ value: '5', label: '5' }],
 *   sentiment: [],
 *   systemTag: [{ value: 'Safety Issue', label: 'Safety Issue' }],
 * };
 */
export interface UnappliedFilters {
    rating: Option[];
    comments: Option[];
    driverTag: Option[];
    sentiment: Option[];
    systemTag: Option[];
    reservationType: Option[];
    startDate: string;
    endDate: string;
}
/**
 * Represents the applied subset of filter fields relevant to the Filter Drawer.
 * This excludes pagination, sorting, limit, and search text.
 *
 * Derived from the broader `Filters` interface by omitting:
 * - searchText
 * - sortBy
 * - offset
 * - limit
 */
export type FilterDrawerFilters = Omit<
    Filters,
    'searchText' | 'sortBy' | 'offset' | 'limit'
>;
/**
 * The initial values for all unapplied filters in the Filter Drawer UI.
 * Used to initialize filter state and to compare against the current state.
 */
export const FilterDrawerFiltersInitialValues: FilterDrawerFilters = {
    startDate,
    endDate,
    rating: [],
    comments: [],
    driverTag: [],
    sentiment: [],
    systemTag: [],
    reservationType: [],
};

/**
 * Transforms the current unapplied filter state (from UI selection)
 * into a normalized, backend-ready object that conforms to the
 * `FilterDrawerFilters` type.
 *
 * Converts all Option[] values to raw string or number arrays, and
 * passes startDate and endDate through unchanged.
 *
 * @param {UnappliedFilters} unapplied - The current unapplied filter state from the drawer.
 * @returns {FilterDrawerFilters} A fully transformed filter object ready to be applied.
 *
 * @example
 * const unapplied = {
 *   rating: [{ value: '5', label: '5 Stars' }],
 *   sentiment: [],
 * };
 * const applied = selectedFilters(unapplied);
 * // {
 * // rating: [5],
 * // sentiment: [],
 * // }
 */
export const selectedFilters = (
    unapplied: UnappliedFilters
): FilterDrawerFilters => ({
    rating: unapplied.rating.map((opt) => Number(opt.value)),
    comments: unapplied.comments.map((opt) => String(opt.value)),
    driverTag: unapplied.driverTag.map((opt) => String(opt.value)),
    sentiment: unapplied.sentiment.map((opt) => String(opt.value)),
    systemTag: unapplied.systemTag.map((opt) => String(opt.value)),
    reservationType: unapplied.reservationType.map((opt) => String(opt.value)),
    startDate: unapplied.startDate,
    endDate: unapplied.endDate,
});

/**
 * Counts how many filter values in the current applied filters differ
 * from the static initial values defined in `FilterDrawerFiltersInitialValues`.
 *
 * This only compares the subset of filters that are managed by the Filter Drawer UI —
 * specifically: startDate, endDate, rating, comments, driverTag, sentiment, systemTag, reservationType.
 *
 * Comparison logic:
 * - For arrays: checks if the length or values differ (order-sensitive).
 * - For strings: uses strict equality.
 *
 * This is useful for determining whether the user has modified any of the drawer filters,
 * and to show the number of filters applied badge on the button
 *
 * @param {Filters} current - The currently applied filters object.
 * @returns {number} The number of drawer filters that differ from their initial values.
 *
 */
export const countChangedDrawerFilters = (current: Filters): number => {
    const filters = omit(current, ['searchText', 'sortBy', 'offset', 'limit']);
    const filtersArray = Object.values(filters).sort();
    const initialValuesArray = Object.values(
        FilterDrawerFiltersInitialValues
    ).sort();
    const diff = differenceWith(filtersArray, initialValuesArray, isEqual);
    const count = diff.length;

    return count;
};
